import { useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, CheckCheck, MessageSquare, ClipboardList, Loader2, Trash2, CalendarCheck, Trophy, PackageX, CalendarClock, ShieldAlert } from "lucide-react";
import { api } from "../../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import "./NotificationBell.css";

const POLL_MS = 45000;

const kindIcon = (kind) => {
  if (kind === "project.comment") return MessageSquare;
  if (kind === "project.status") return Loader2;
  if (kind === "cuti.status") return CalendarCheck;
  if (kind === "rank.change") return Trophy;
  if (kind === "stok.kritis") return PackageX;
  if (kind === "utang.jatuh_tempo") return CalendarClock;
  // Google Authenticator dipasang/diputus, password direset lewat kode,
  // reset dikunci (routers/authenticator.py::_kabari_pemilik).
  if (kind === "akun.keamanan") return ShieldAlert;
  return ClipboardList;
};
const kindTint = (kind) => {
  if (kind === "project.comment") return "bg-sky-100 text-sky-700";
  if (kind === "project.status") return "bg-amber-100 text-amber-700";
  if (kind === "cuti.status") return "bg-violet-100 text-violet-700";
  if (kind === "rank.change") return "bg-yellow-100 text-yellow-700";
  // Peringatan (bukan info biasa) — stok kritis & utang jatuh tempo butuh
  // perhatian segera, jadi dibedakan dari tint netral kind lain.
  if (kind === "stok.kritis") return "bg-orange-100 text-orange-700";
  if (kind === "utang.jatuh_tempo") return "bg-rose-100 text-rose-700";
  // Keamanan akun: pemilik yang tidak merasa melakukannya harus bertindak.
  if (kind === "akun.keamanan") return "bg-rose-100 text-rose-700";
  return "bg-emerald-100 text-emerald-700";
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}d lalu`;
  if (s < 3600) return `${Math.floor(s / 60)}m lalu`;
  if (s < 86400) return `${Math.floor(s / 3600)}j lalu`;
  return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short" });
}

export default function NotificationBell({ dark = false, showLabel = false, side = "bottom" }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const titleId = useId();

  const loadCount = async () => {
    try {
      const { data } = await api.get("/notifications/count");
      setCount(data.unread || 0);
    } catch { /* silent */ }
  };
  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(data);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => {
    loadCount();
    const iv = setInterval(loadCount, POLL_MS);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (open) loadList();
  }, [open]);

  const markAll = async () => {
    try {
      await api.post("/notifications/mark-read", {});
      setCount(0);
      setItems(s => s.map(x => ({ ...x, read: true })));
    } catch { /* silent */ }
  };

  const clearRead = async () => {
    try {
      await api.delete("/notifications");
      setItems(s => s.filter(x => !x.read));
    } catch { /* silent */ }
  };

  const clickItem = async (n) => {
    if (!n.read) {
      try {
        await api.post("/notifications/mark-read", { ids: [n.id] });
        setCount(c => Math.max(0, c - 1));
        setItems(s => s.map(x => x.id === n.id ? { ...x, read: true } : x));
      } catch { /* silent */ }
    }
    setOpen(false);
    if (n.link) nav(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
      <button
        type="button"
        data-testid="notif-bell"
        className={`eh-notification-trigger relative h-9 w-9 rounded-lg grid place-items-center transition-colors ${
          dark ? "text-white/70 hover:text-white hover:bg-white/10" : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
        }`}
        aria-label="Notifikasi"
      >
        <Bell className="w-[18px] h-[18px]" />
        {count > 0 && (
          <span data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center leading-none ring-2 ring-white">
            {count > 99 ? "99+" : count}
          </span>
        )}
        {showLabel && <span className="ml-2 text-sm">Notifikasi</span>}
      </button>
      </PopoverTrigger>
          <PopoverContent
            side={side}
            align="end"
            sideOffset={12}
            collisionPadding={12}
            sticky="always"
            aria-labelledby={titleId}
            data-testid="notif-popover"
            className="eh-notifications no-print z-[80] w-[380px] p-0"
          >
            <div className="eh-notifications-heading">
              <div>
                <h2 id={titleId}>Notifikasi</h2>
                <p>{count} belum dibaca</p>
              </div>
              <div className="flex items-center gap-1">
                {count > 0 && (
                  <button onClick={markAll} data-testid="notif-mark-all"
                    title="Tandai semua sudah dibaca"
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-emerald-700">
                    <CheckCheck className="w-4 h-4" />
                  </button>
                )}
                <button onClick={clearRead} data-testid="notif-clear-read"
                  title="Hapus yang sudah dibaca"
                  className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-rose-600">
                  <Trash2 className="w-4 h-4" />
                </button>
                <button onClick={() => setOpen(false)} aria-label="Tutup notifikasi" data-testid="notif-close" className="p-1.5 rounded hover:bg-slate-100 text-slate-400">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="eh-notifications-list" data-testid="notif-list">
              {loading ? (
                <div role="status" className="py-8 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" /> Memuat...
                </div>
              ) : items.length === 0 ? (
                <div className="py-12 text-center">
                  <Bell className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Belum ada notifikasi</p>
                  <p className="text-xs text-slate-400 mt-1">Update tugas &amp; komentar akan muncul di sini.</p>
                </div>
              ) : (
                <ul>
                  {items.map(n => {
                    const Icon = kindIcon(n.kind);
                    return (
                      <li key={n.id}>
                        <button
                          onClick={() => clickItem(n)}
                          data-testid={`notif-${n.id}`}
                          className={`eh-notification-item ${n.read ? "is-read" : "is-unread"}`}
                        >
                          <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${kindTint(n.kind)}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className="eh-notification-title">{n.title}</p>
                              {!n.read && <span className="eh-notification-unread" aria-label="Belum dibaca" />}
                            </div>
                            {n.body && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>}
                            <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </PopoverContent>
    </Popover>
  );
}
